import React, { useEffect, useMemo } from 'react';
import { CONTENT_TYPE } from 'src/constants';
import { IContent, IResponseList } from 'src/interface';
import { Logger } from 'src/utils';
import { listReducer, listActions, IListState } from './store';

export interface ListActions {
    onPageChange: (page: number) => void;
    onFilterChange: (param: any) => any;
    onAddItem: (item: object, index?: number) => void;
    onRemoveItem: (item: object, keyId?: string) => void;
    onEditItem: (item: object, keyId?: string) => void;
    clearItems: () => void;
    refresh: (initState: any) => void;
    reset: (initState: any) => void;
}

export interface IProps<T> {
    fetchFn: (...params: any) => Promise<IResponseList<T>>;
    initState?: any;
}

export const STATE_DEFAULT = {
    items: [],
    total: 0,
    filters: {
        sort: 'listen',
    },
    pageSize: 1,
    currentPage: 1,
    isFetching: true,
    hasMore: false,
    refresh: 0,
    sectionId: '',
};

export function useList<T>(
    props: IProps<T>,
    deps: any = []
): [IListState<T>, ListActions] {
    const { fetchFn, initState = {} } = props;
    const [list, dispatch] = React.useReducer(
        listReducer,
        Object.assign({}, STATE_DEFAULT, initState)
    );

    const { refresh, currentPage, filters } = list;

    const actions = useMemo<ListActions>(() => {
        const a = {
            onPageChange: (page: number = 1) => {
                Logger.debug('useList | onPageChange | current:', page);
                dispatch(listActions.setCurrentPageAction(page));
            },

            onAddItem: (item: object, index = 0) => {
                Logger.debug('useList | onAddItem:', item);
                dispatch(listActions.addItemAction(item, index));
            },
            onRemoveItem: (item: object, keyId: string = 'encodeId') => {
                Logger.debug('useList | onRemoveItem:', item);
                dispatch(listActions.removeItemAction(item, keyId));
            },
            clearItems: () => {
                Logger.debug('useList | clearItems');
                dispatch(listActions.clearItems());
            },
            refresh: (initState: any) => {
                dispatch(listActions.refresh(initState));
            },
            reset: (initState: any) => {
                dispatch(listActions.reset(initState));
            },
            onFilterChange: (filters: any = {}) => {
                Logger.debug('useList | filter:', filters);
                dispatch(listActions.setFiltersAction(filters));
            },
        };
        return a as ListActions;
    }, []);

    useEffect(() => {
        dispatch(listActions.setLoadingAction(true));
        const listContent = list.items.filter((item: IContent) => {
            return item.type !== CONTENT_TYPE.AD_BANNER && item.type !== CONTENT_TYPE.AD_NATIVE
        })
        const index = listContent.length;
        fetchFn(currentPage, filters, index)
            .then((resp: IResponseList<T>) => {
                Logger.debug('useFetch : data', resp.data);
                dispatch(listActions.fetchSuccessAction(resp.data || {}));
            })
            .catch((error) => {
                if (error) {
                    dispatch(listActions.fetchErrorAction(error));

                    if (error.err) {
                        Logger.error(error.err, 'useFetch Fail', error);
                    }
                }
            });
    }, [refresh, currentPage, filters, ...deps]);

    return [list, actions];
}

export default useList;
